import { SCATTER_CHART_OPTIONS } from '../../../../common/utils/constants'
import {
  AxisValues,
  ChartElements,
  disaggregations,
} from '../../../../common/utils/enum'
import {
  formatNumberOnUnitMeasure,
  formatUnitMeasure,
  formatValues,
  getBadges,
  getNameFromDisaggregation,
  getUnitMeasureItems,
  getRecentYearRange,
  applyAxisRange,
  formatUnitMult,
} from '../../../../common/utils/helper'
import { COLOR_MAP } from '../../../../common/utils/theme'
import { getChartBadges, getChartTitle } from '../../common/helper'

const getSubtitleForLogChart = (
  countries,
  selectedYear,
  recentYearSelection,
  filteredIndicatorData
) => {
  const formattedCountries = countries.join(', ')
  const subtitle = formattedCountries
    ? selectedYear
      ? `${formattedCountries}, ${
          recentYearSelection
            ? `Most recent values ${getRecentYearRange(filteredIndicatorData)}`
            : selectedYear
        }`
      : formattedCountries
    : selectedYear
      ? `${
          recentYearSelection
            ? `Most recent values ${getRecentYearRange(filteredIndicatorData)}`
            : selectedYear
        }`
      : ''
  return subtitle
}

const getScatterChartSubtitle = (
  countries,
  selectedYear,
  badges,
  recentYearSelection,
  filteredIndicatorData
) => {
  const formattedCountries = countries.join(', ')
  const subtitle = formattedCountries
    ? selectedYear
      ? `${formattedCountries}, ${
          recentYearSelection
            ? `Most recent values ${getRecentYearRange(filteredIndicatorData)}`
            : selectedYear
        }`
      : formattedCountries
    : selectedYear
      ? `${
          recentYearSelection
            ? `Most recent values ${getRecentYearRange(filteredIndicatorData)}`
            : selectedYear
        }`
      : ''

  // TO-DO: show the unit multipliers on axes
  let badgesDiv =
    badges && badges.length > 0
      ? badges.map(
          (badge) =>
            `<div class="text tui_badge">
        <div>
          <p>${badge}</p>
        </div>
      </div>`
        )
      : []
  badgesDiv = badgesDiv.join(' ')
  return {
    useHTML: true,
    text: `<p class="tui__text_base">${subtitle}</p>
      <div class="tui__badge_container">
        <div class="tui_badge_group">
            ${badgesDiv}
        </div>
      </div>
    `,
    align: 'left',
  }
}

export const generateScatterChartOptions = (
  indicatorDataForChart,
  secondIndicatorData,
  indicatorMetadata,
  countriesData,
  selectedCountries,
  selectedYear,
  selectedComparison,
  selectedSecondIndicator,
  selectedFilters,
  disaggregationData,
  filteredAggregateData,
  recentYearSelection,
  unitMeasureName,
  selectedUnitMeasure,
  secondDisaggregationData,
  chartContainerId,
  unitMeasureList,
  site,
  selectedOnlyShow,
  regionHierarchy,
  incomeGroupHierarchy
) => {
  let seriesData = []
  const shortLabel = getUnitMeasureItems(disaggregationData)?.find(
    (item) => item.code === selectedUnitMeasure
  )?.short_label

  if (
    indicatorDataForChart &&
    secondIndicatorData &&
    countriesData &&
    selectedCountries
  ) {
    const groupsMap = {}
    const isOnlyShowIncomeGroup =
      incomeGroupHierarchy
        ?.map((group) => group.id)
        ?.includes(selectedOnlyShow) || false
    const regionIncomeGroupCountriesMapping = isOnlyShowIncomeGroup
      ? incomeGroupHierarchy
      : regionHierarchy
    regionIncomeGroupCountriesMapping.forEach((group) => {
      groupsMap[group.id] = {
        name: group.id, // getNameFromCodelist(codelist, 'REF_AREA', group.id),  //to be uncommented if region name is needed
        regionId: group.id,
        color: COLOR_MAP[group.id],
        marker: {
          symbol: 'circle',
          radius: 3.75,
        },
        data: [],
      }

      group.countries.forEach((country) => {
        const countryCode = country?.id
        const countryName = country?.name

        const indicatorData = indicatorDataForChart.find(
          (item) => item[disaggregations.RefArea] === countryCode
        )

        let xYear = indicatorData?.data?.xAxis?.[0]
        let xValue = indicatorData?.data?.yAxis?.[0]

        xValue = xValue && xValue !== 'null' && xValue !== 'NaN' ? xValue : null

        const secIndicatorData = secondIndicatorData.find(
          (item) => item[disaggregations.RefArea] === countryCode
        )

        let yYear = secIndicatorData?.data?.xAxis?.[0]
        let yValue = secIndicatorData?.data?.yAxis?.[0]
        yValue = yValue && yValue !== 'null' && yValue !== 'NaN' ? yValue : null

        if (xValue && yValue) {
          groupsMap[group.id].data.push({
            x: formatValues(xValue),
            y: formatValues(yValue),
            xYear: xYear,
            yYear: yYear,
            country: countryName,
            className: selectedCountries?.includes(countryCode)
              ? 'selected-country-point'
              : 'scatter-point',
            marker: {
              lineWidth: selectedCountries?.includes(countryCode) ? 3 : 0,
              lineColor: selectedCountries?.includes(countryCode)
                ? COLOR_MAP['highlight']
                : undefined,
              radius: selectedCountries?.includes(countryCode) ? 5 : 3.75,
            },
            unitMeasureCode: indicatorData?.[disaggregations.UnitMeasure],
          })
        }
      })
    })

    seriesData = Object.values(groupsMap)
  }
  const badges = getBadges(
    selectedFilters,
    disaggregationData,
    selectedComparison,
    unitMeasureName
  )

  const firstIndicatorAxisValues =
    indicatorMetadata?.admin_metadata?.[site]?.visualization?.axis_values || ''
  const firstIndicatorExpNumber =
    indicatorMetadata?.admin_metadata?.[site]?.visualization?.axis_values_params
  const secondIndicatorAxisValues =
    selectedSecondIndicator?.visualization?.axis_values
  const secondIndicatorExpNumber =
    selectedSecondIndicator?.visualization?.axis_values_params

  const values = seriesData
    .flatMap((item) => item?.data)
    .map((item) => (item?.x ? parseFloat(item.x) : null))
  const plotlineValues = filteredAggregateData.flatMap((item) =>
    item?.data?.yAxis[0] ? parseFloat(item.data.yAxis[0]) : null
  )

  const min = Math.min(...values, ...plotlineValues)
  const max = Math.max(...values, ...plotlineValues)

  const chartWidth = document.getElementById(chartContainerId)
    ? document.getElementById(chartContainerId).getBoundingClientRect()?.width
    : 1
  let minPixelGap = 23
  if (window.innerWidth >= 600) {
    minPixelGap = 15
  } else if (window.innerWidth < 600 && window.innerWidth >= 500) {
    minPixelGap = 17
  }
  const threshold = ((max - min) / chartWidth) * minPixelGap
  // const threshold = ((max - min) / chartWidth) * 25
  let lastLabelOffset = 0,
    previousValues = []

  const sortedPlotLines = filteredAggregateData
    .map((item) => ({
      ...item,
      value: item?.data?.yAxis?.[0] ? parseFloat(item.data.yAxis[0]) : null, // Use 0 if yAxis is undefined
    }))
    .sort((a, b) => a.value - b.value)
  const plotLines = sortedPlotLines.map((item, index) => {
    // get 0th index since we are passing selected year to api and it gives only 1 result for data.xAxis and data.yAxis
    const value = item.data?.yAxis?.[0]
    if (
      (firstIndicatorAxisValues &&
        (firstIndicatorAxisValues === AxisValues.Logarithmic ||
          firstIndicatorAxisValues === AxisValues.Exponential)) ||
      (secondIndicatorAxisValues &&
        (secondIndicatorAxisValues === AxisValues.Logarithmic ||
          secondIndicatorAxisValues === AxisValues.Exponential))
    ) {
      return {
        value: formatValues(value),
        label: `${getNameFromDisaggregation(
          disaggregationData?.dimensions,
          disaggregations.RefArea,
          item[disaggregations.RefArea]
        )?.toUpperCase()} ${formatNumberOnUnitMeasure({
          value,
          unitMeasure: item?.[disaggregations.UnitMeasure],
          decimals: item?.[disaggregations.Decimals],
        })} `,
      }
    } else {
      // logic to arrange all the info icons and make them visible
      let overlapCount = 0
      const currentValue = parseFloat(value)
      previousValues.forEach((prev) => {
        if (Math.abs(currentValue - prev.value) < threshold) {
          overlapCount++
        }
      })
      let labelOffset = -10 + overlapCount * 22

      if (lastLabelOffset === labelOffset && overlapCount) {
        labelOffset =
          previousValues[previousValues.length - overlapCount - 1]?.labelOffset
      }
      lastLabelOffset = labelOffset
      previousValues.push({ value: currentValue, labelOffset })

      return {
        value: formatValues(value),
        dashStyle: 'LongDash',
        // color: 'rgba(0, 0, 0, 0.24)',
        color: COLOR_MAP['benchmark'],
        label: {
          useHTML: true,
          text: `<div style="
    font-weight: 700; 
    color: black; 
    max-width: 400px; 
    white-space: normal; 
    word-wrap: break-word;
  ">
     
          <span 
        class="plotline-info-icon"
        style="cursor: pointer"
        id="${`${getNameFromDisaggregation(
          disaggregationData?.dimensions,
          disaggregations.RefArea,
          item[disaggregations.RefArea]
        )?.toUpperCase()} ${formatNumberOnUnitMeasure({
          value,
          unitMeasure: item?.[disaggregations.UnitMeasure],
          decimals: item?.[disaggregations.Decimals],
        })} `}" 
      ><i class="lp lp-info-circle-solid"></i></span>
  </div>`,
          rotation: 0,
          // rotation: -90,
          align: 'right',
          // textAlign: 'center',
          // verticalAlign: 'bottom',
          // y: -10,
          // x: -5,
          y: labelOffset,
          x: 5,
        },
      }
    }
  })

  const subtitleCountries = selectedCountries?.map(
    (selectedCountry) =>
      countriesData.find(
        (country) =>
          country.code.toLowerCase() === selectedCountry.toLowerCase()
      )?.description
  )

  const secondIndicatorUnitMeasureName =
    getUnitMeasureItems(secondDisaggregationData)?.find(
      (item) =>
        item?.code === secondIndicatorData?.[0]?.[disaggregations.UnitMeasure]
    )?.description ?? ''

  let scatterChartOptions = {}
  if (
    (firstIndicatorAxisValues &&
      (firstIndicatorAxisValues === AxisValues.Logarithmic ||
        firstIndicatorAxisValues === AxisValues.Exponential)) ||
    (secondIndicatorAxisValues &&
      (secondIndicatorAxisValues === AxisValues.Logarithmic ||
        secondIndicatorAxisValues === AxisValues.Exponential))
  ) {
    scatterChartOptions = {
      title: indicatorMetadata?.admin_metadata?.[site]?.visualization
            ?.display_name || indicatorMetadata?.series_description.name,
      subtitle: getSubtitleForLogChart(
        subtitleCountries,
        selectedYear,
        recentYearSelection,
        indicatorDataForChart
      ),
      badges: getChartBadges(
        selectedFilters,
        disaggregationData,
        selectedComparison,
        unitMeasureName
      ),
      series: seriesData,
      indicatorCode: indicatorMetadata?.series_description.idno,
      indicatorName:
        indicatorMetadata?.admin_metadata?.[site]?.visualization
          ?.display_name || indicatorMetadata?.series_description?.name,
      secondIndicatorName: selectedSecondIndicator?.name,
      recentYearSelection,
      unitMeasureName,
      unitMultName: formatUnitMult(
        disaggregationData,
        indicatorDataForChart?.[0]?.[disaggregations.UnitMult]
      ),
      secondIndicatorUnitMeasureName,
      selectedUnitMeasure,
      selectedSecondUnitMeasure:
        secondIndicatorData?.[0]?.[disaggregations.UnitMeasure],
      selectedYear,
      benchmarkPlotLines: plotLines,
    }
  } else {
    scatterChartOptions = {
      ...SCATTER_CHART_OPTIONS,
      chart: {
        ...SCATTER_CHART_OPTIONS.chart,
        events: {
          render: function () {
            document.querySelectorAll('.plotline-info-icon').forEach((el) => {
              el.onclick = null
            })

            document.querySelectorAll('.plotline-info-icon').forEach((el) => {
              el.onclick = function (e) {
                let icon = e.target.closest('.plotline-info-icon')
                const chartContainer = document.getElementById(
                  'scatter_chart_container'
                )
                const plotLineLabel = icon ? icon.getAttribute('id') : null
                if (plotLineLabel) {
                  document
                    .querySelectorAll('.plotline-tooltip')
                    .forEach((tooltip) => {
                      tooltip.remove()
                    })
                  const tooltip = document.createElement('div')
                  tooltip.innerText = plotLineLabel
                  tooltip.style.display = 'block'
                  tooltip.style.position = 'absolute'
                  tooltip.style.background = '#ffffff'
                  tooltip.style.marginRight = '20px'
                  tooltip.style.border = '1px solid #ccc'
                  tooltip.style.padding = '4px 8px'
                  tooltip.style.boxShadow =
                    'rgba(0, 0, 0, 0.15) 4px 4px 4px 0px'
                  tooltip.style.fontSize = '14px'
                  tooltip.style.zIndex = 1
                  tooltip.className = 'plotline-tooltip tui_chart_tooltip_title'

                  const padding = 8
                  const iconRect = icon.getBoundingClientRect()
                  const containerRect = chartContainer.getBoundingClientRect()
                  const hasSpaceOnRight =
                    window.innerWidth - iconRect.right > 130
                  tooltip.style.top = iconRect.top - containerRect.top + 'px'
                  if (hasSpaceOnRight) {
                    tooltip.style.left =
                      iconRect.right - containerRect.left + 'px'
                    chartContainer.append(tooltip)
                  } else {
                    tooltip.style.visibility = 'hidden'
                    chartContainer.append(tooltip)
                    const tooltipWidth = tooltip.offsetWidth
                    tooltip.style.left =
                      iconRect.left - tooltipWidth - padding + 'px'
                    tooltip.style.visibility = 'visible'
                  }

                  document.addEventListener(
                    'click',
                    function removeTooltip(ev) {
                      if (
                        !tooltip.contains(ev.target) &&
                        !icon.contains(ev.target)
                      ) {
                        tooltip.remove()
                        document.removeEventListener('click', removeTooltip)
                      }
                    }
                  )
                }
              }
            })
          },
        },
      },
      // title: getScatterChartTitle(selectedSecondIndicator, selectedYear),
      title: getChartTitle(
            indicatorMetadata?.admin_metadata?.[site]?.visualization
              ?.display_name || indicatorMetadata?.series_description.name
          ),
      subtitle: getScatterChartSubtitle(
        subtitleCountries,
        selectedYear,
        badges,
        recentYearSelection,
        indicatorDataForChart
      ),
      exporting: { enabled: false },
      xAxis: {
        title: {
          text:
            indicatorMetadata?.admin_metadata?.[site]?.visualization
              ?.display_name || indicatorMetadata?.series_description?.name,
          style: {
            color: 'black',
            fontWeight: '600',
          },
        },
        tickLength: 0,
        gridLineWidth: 1,
        endOnTick: false,
        lineColor: 'transparent',
        gridLineColor: COLOR_MAP[ChartElements.GridLine],
        labels: {
          style: { color: 'rgba(0,0,0,0.6)' },
           formatter: function () {
            return formatNumberOnUnitMeasure({
              value: this.value,
              unitMeasure: selectedUnitMeasure,
              decimals: indicatorDataForChart?.[0]?.[disaggregations.Decimals],
              axis: this.axis,
            })
          },
        },
      },
      yAxis: {
        endOnTick: false,
        title: {
          text: selectedSecondIndicator?.name,
          style: {
            color: 'black',
            fontWeight: '600',
          },
        },
        gridLineColor: COLOR_MAP[ChartElements.GridLine],
        labels: {
          style: { color: 'rgba(0,0,0,0.6)', fontSize: '0.9rem' },
           formatter: function () {
            return formatNumberOnUnitMeasure({
              value: this.value,
              unitMeasure:
                secondIndicatorData?.[0]?.[disaggregations.UnitMeasure],
              decimals: secondIndicatorData?.[0]?.[disaggregations.Decimals],
              axis: this.axis,
            })
          },
        },
        plotLines: [
          {
            value: 0,
            color: COLOR_MAP[ChartElements.ZeroLine],
            zIndex: 5,
          },
        ],
      },
      tooltip: {
        ...SCATTER_CHART_OPTIONS.tooltip,
        useHTML: true,
        backgroundColor: null,
        borderWidth: 0,
        shadow: false,
        style: {
          padding: 0,
        },
        formatter: function () {
          const title = `<div class="tui_chart_tooltip_title" style="font-size: 14px; font-weight: 600; line-height: 16px; color: rgba(0, 0, 0, 0.87); padding-bottom: 4px; margin-bottom: 4px; border-bottom: 1px solid #CED4DE;">${
            this.point.country
          } ${recentYearSelection ? '' : `, ${selectedYear}`}</div>`

          const content = `
      <li style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 4px; min-width: 230px;">
        <div class="tooltip_country" style="display: flex; align-items: center;  padding-right: 8px;  ">
                     ${
                       recentYearSelection
                         ? `${
                             indicatorMetadata?.admin_metadata?.[site]
                               ?.visualization?.display_name ||
                             indicatorMetadata?.series_description?.name
                           } (${this.point.xYear})`
                         : indicatorMetadata?.admin_metadata?.[site]
                             ?.visualization?.display_name ||
                           indicatorMetadata?.series_description?.name
                     }
        </div>
        <span class="tooltip_value" style="font-size: 16px; font-weight: 700; line-height: 20px; color: rgba(0, 0, 0, 0.87);">
         ${formatNumberOnUnitMeasure({
           value: this.point.x,
           unitMeasure: selectedUnitMeasure || this.point?.unitMeasureCode,
           decimals: indicatorDataForChart?.[0]?.[disaggregations.Decimals],
           isCommaSeparatedFormat: true,
         })} ${formatUnitMult(
           disaggregationData,
           indicatorDataForChart?.[0]?.[disaggregations.UnitMult]
         )} ${formatUnitMeasure(
           selectedUnitMeasure || this.point?.unitMeasureCode,
           unitMeasureName ||
             unitMeasureList.find(
               (item) => item.code === this.point?.unitMeasureCode
             )?.description ||
             '',
           shortLabel ||
             unitMeasureList.find(
               (item) => item.code === this.point?.unitMeasureCode
             )?.short_label ||
             ''
         )}  
        </span>
      </li>
      <li style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 4px;" min-width: 230px;>
        <div class="tooltip_country" style="display: flex; align-items: center;  padding-right: 8px;">
                     ${
                       recentYearSelection
                         ? `${selectedSecondIndicator?.name} (${this.point.yYear})`
                         : selectedSecondIndicator?.name
                     }
        </div>
        <span class="tooltip_value" style="font-size: 16px; font-weight: 700; line-height: 20px; color: rgba(0, 0, 0, 0.87);">
          ${formatNumberOnUnitMeasure({
            value: this.point.y,
            unitMeasure:
              secondIndicatorData?.[0]?.[disaggregations.UnitMeasure],
            decimals: secondIndicatorData?.[0]?.[disaggregations.Decimals],
            isCommaSeparatedFormat: true,
          })} ${formatUnitMult(
            secondDisaggregationData,
            secondIndicatorData?.[0]?.[disaggregations.UnitMult]
          )} ${formatUnitMeasure(
            secondIndicatorData?.[0]?.[disaggregations.UnitMeasure],
            secondIndicatorUnitMeasureName,
            getUnitMeasureItems(secondDisaggregationData)?.find(
              (item) =>
                item.code ===
                secondIndicatorData?.[0]?.[disaggregations.UnitMeasure]
            )?.short_label
          )}  
        </span>
      </li>`

          return `
      <div class="tui_chart_tooltip" style="border: 0.5px solid #CED4DE; background: #FFF; padding: 6px 8px; box-shadow: 4px 4px 4px 0px rgba(0, 0, 0, 0.15); width: auto;">
        ${title}
        <ul style="padding-left: 0; list-style: none; margin: 0;">
          ${content}
        </ul>
      </div>
    `
        },
      },
      series: seriesData,
    }
    if (plotLines && plotLines.length > 0) {
      scatterChartOptions.xAxis.plotLines = [
        ...plotLines,
        {
          value: 0,
          color: COLOR_MAP[ChartElements.ZeroLine],
          zIndex: 5,
        },
      ]
    } else {
      scatterChartOptions.xAxis.plotLines = [
        {
          value: 0,
          color: COLOR_MAP[ChartElements.ZeroLine],
          zIndex: 5,
        },
      ]
    }

    applyAxisRange({
      visualizationMetadata:
        indicatorMetadata?.admin_metadata?.[site]?.visualization,
      chartOptions: scatterChartOptions,
      axisKey: 'xAxis',
      axisDimension: 'x',
      scatterPlotlineData: plotLines,
    })

    applyAxisRange({
      visualizationMetadata: selectedSecondIndicator?.visualization,
      chartOptions: scatterChartOptions,
      axisKey: 'yAxis',
      axisDimension: 'y',
    })
  }
  return scatterChartOptions
}
